import { FC, useMemo, useRef } from 'react'

import { TableRef, Token } from '@revolut-internal/ui-kit'

import { LimitedTable } from 'modules/Monitoring/components/LimitedTable/LimitedTable'
import { TypedPrimaryCell } from 'modules/Monitoring/components/TableCells/TypedPrimaryCell'
import { useChartTableSelectedRows } from 'modules/Monitoring/hooks/useChartTableSelectedRows'
import { useTableState } from 'modules/Monitoring/hooks/useTableState'
import { ChartTableProps, TableDataItem } from 'modules/Monitoring/types'
import { UrlStateStorageKeys } from 'modules/Monitoring/url-state-storage/consts'
import { getFullRangeHeatMapColor } from 'modules/Monitoring/utils/charts/getFullRangeHeatMapColor'
import { getPositiveHeatMapColor } from 'modules/Monitoring/utils/charts/getPositiveHeatMapColor'
import { prepareTableColumnsFromChartData } from 'modules/Monitoring/utils/charts/prepareTableColumnsFromChartData'
import { prepareTableDataFromChartData } from 'modules/Monitoring/utils/charts/prepareTableDataFromChartData'
import { formatNumber } from 'modules/Monitoring/utils/formatNumber'

import { AttendanceVariant, AttendanceVariants } from './types'

type AgentAttendanceIntervalsTableProps = ChartTableProps & {
  attendanceVariant: AttendanceVariant
}

export const AgentAttendanceIntervalsTable: FC<AgentAttendanceIntervalsTableProps> = ({
  data,
  charts,
  attendanceVariant,
  selectedCharts,
  onSelectCharts,
}) => {
  const tableRef = useRef<TableRef<TableDataItem>>(null)
  const preparedData = useMemo(() => prepareTableDataFromChartData(data), [data])

  const { state: tableState, setSorting } = useTableState(
    UrlStateStorageKeys.AgentAttendanceDetailsState,
  )
  const { selectedRowIds, onSelect } = useChartTableSelectedRows({
    data: preparedData,
    selectedCharts,
    charts,
    onSelectCharts,
  })

  const columns = useMemo(
    () =>
      prepareTableColumnsFromChartData({
        data,
        selectedRowIds,
        firstColumnLabel: 'Shift schedule',
        onRowSelect: onSelect,
        renderCell: ({ cell, value }) => (
          <TypedPrimaryCell
            cell={cell}
            style={{
              height: '100%',
              padding: `0 ${Token.space.s12}`,
              width: `calc(100% + 24px)`,
              margin: `0 ${Token.space.n12}`,
              backgroundColor:
                attendanceVariant === AttendanceVariants.Scheduled
                  ? getPositiveHeatMapColor(value.hasData ? Number(value.value) : 0)
                  : getFullRangeHeatMapColor(value.hasData ? Number(value.value) : 0),
            }}
          >
            {value.hasData ? formatNumber(value.value) : '-'}
            {attendanceVariant === AttendanceVariants.Adherence && value.hasData && ' %'}
          </TypedPrimaryCell>
        ),
      }),
    [data, attendanceVariant, selectedRowIds, onSelect],
  )

  return (
    <LimitedTable
      ref={tableRef}
      state={tableState}
      columns={columns}
      data={preparedData}
      rowsToShow={12}
      aria-label="Interval attendance table"
      primaryColumnId="name"
      resizable
      hideGradient
      enableMultiSort
      onSortingChange={setSorting}
    />
  )
}
